import { Component, Fragment } from 'react';
import styled, { css } from 'styled-components';
import { IconPlay } from '@yj-intl/riff-react';
import { size } from '../../../../../common/css/setting/variable';
import { IConvertedImages } from '../../../../../common/store/interface/IConvertedImages';
import { mixinThumbnailTimeLabel, mixinThumbnailLine } from '../../../../../common/css/setting/mixin';
import ThumbnailHelper, {
  DEFAULT_IMAGE_4x3,
  DEFAULT_IMAGE_1x1,
  DEFAULT_IMAGE_19x10
} from '../../../../../common/thumbnail/ThumbnailHelper';
import ThumbnailLoadCheckModule from '../../../../../common/thumbnail/ThumbnailLoadCheckModule';

export const ImageSize = {
  SMALL: 'small' /* 右カラム、トピ一覧などで使われるサイズ */,
  MIDDLE: 'middle' /* ニュースフィードなどで使われるサイズ */,
  LARGE: 'large' /* 速報などで使われるサイズ */,
  WIDE: 'wide' /* ニュースフィードの横長版などで使われる横幅いっぱいサイズ */,
  WIDE_19X10: 'wide_19x10' /* ヤフコメタイムラインなどで使われるサイズ */
} as const;

export type ImageSize = (typeof ImageSize)[keyof typeof ImageSize];

interface ThumbnailProps {
  readonly imageSize?: ImageSize;
  readonly imageWidth?: string;
  readonly imageHeight?: string;
  readonly imageSrc?: string;
  readonly imageAlt?: string;
  readonly resizedImageSrc: IConvertedImages | null;
  readonly thumbnailBackgroundColor?: string;
  // loading attributeの指定をしないフラグ / firstViewのコンテンツのときtrueにするのを推奨
  readonly disableLazyLoading?: boolean;
}

type ThumbnailPropsWithoutVideo = ThumbnailProps & {
  readonly hasVideo: false;
};

type ThumbnailPropsWithVideo = ThumbnailProps & {
  readonly hasVideo: true;
  readonly videoDuration?: string;
};

export type Props = ThumbnailPropsWithoutVideo | ThumbnailPropsWithVideo;

interface State {
  readonly onError: boolean;
}

const ThumbnailWrap = styled.div<{
  readonly $hasVideo: boolean;
  readonly $heightSize: string;
  readonly $widthSize: string;
  readonly $backgroundColor?: string;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  overflow: hidden;
  ${mixinThumbnailLine({ width: '100%', height: '100%' })};
  ${({ $hasVideo, $heightSize, $widthSize, $backgroundColor, theme }) =>
    css`
      ${$hasVideo
        ? css`
            background-color: ${theme.news.palette.thumbnail_movie_common};
          `
        : css`
            background-color: ${$backgroundColor || theme.news.palette.thumbnail_normal_common};
          `}
      width: ${$widthSize};
      height: ${$heightSize};
    `}
`;

const ThumbnailImage = styled.img<{
  readonly $heightSize: string;
  readonly $widthSize: string;
}>`
  vertical-align: middle;
  user-select: none;
  /* 小数点以下の扱いによっては座布団が見えることがあるので、画像を1pxはみ出させて非表示に */
  max-height: calc(${props => props.$heightSize} + 1px);
  max-width: calc(${props => props.$widthSize} + 1px);
`;

const VideoInfoCaption = styled.span`
  ${mixinThumbnailTimeLabel({ padding: 4, fontSize: 11 })};
`;

const VideoTimeText = styled.span`
  margin-left: 4px;
`;

/* 再生アイコンRiff */
const StyledIconPlay = styled(IconPlay)`
  margin: 0 -2px;
  color: ${props => props.theme.riff.palette.base};
`;

export default class Thumbnail extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      onError: false
    };
  }
  /**
   *
   */
  public render() {
    const [imageWidth, imageHeight] = this.getImageWidthAndHeight();
    const loadingAttribute: { loading: 'lazy' } | null = this.props.disableLazyLoading ? null : { loading: 'lazy' };

    const thumbnailImageElement = (
      <ThumbnailImage
        {...loadingAttribute}
        src={this.props.imageSrc || ''}
        alt={this.props.imageAlt || ''}
        $widthSize={imageWidth}
        $heightSize={imageHeight}
      />
    );

    // リサイズ画像がある場合pictureタグを用いて掲出
    const imageElement =
      !this.props.resizedImageSrc ||
      this.props.resizedImageSrc.webp === '' ||
      this.props.resizedImageSrc.jpeg === '' ? (
        thumbnailImageElement
      ) : (
        <picture>
          <source type="image/webp" srcSet={this.props.resizedImageSrc.webp} />
          <source type="image/jpeg" srcSet={this.props.resizedImageSrc.jpeg} />
          {thumbnailImageElement}
        </picture>
      );
    // 画像が引けない場合のデフォルト画像
    const thumbnailDefaultImageElement = (
      <ThumbnailImage
        {...loadingAttribute}
        src={this.getDefaultImageUrl()}
        alt=""
        $widthSize={imageWidth}
        $heightSize={imageHeight}
      />
    );

    // class名を使って上書きしているところがあるのでclassNameのthumbnail系は残す
    let thumbnailClassName = 'thumbnail';
    if (this.props.imageSize) {
      thumbnailClassName += ` thumbnail-${this.props.imageSize}`;
    }

    const actionOnError = () => {
      this.setState({ onError: true });
    };

    return (
      <ThumbnailWrap
        className={thumbnailClassName}
        $hasVideo={this.props.hasVideo}
        $widthSize={imageWidth}
        $heightSize={imageHeight}
        $backgroundColor={this.props.thumbnailBackgroundColor}
      >
        {this.state.onError ? (
          thumbnailDefaultImageElement
        ) : (
          <Fragment>
            <ThumbnailLoadCheckModule
              imageSrc={this.props.imageSrc}
              resizedThumbUrl={this.props.resizedImageSrc ?? undefined}
              actionOnError={actionOnError}
            />
            {imageElement}
          </Fragment>
        )}
        {this.props.hasVideo ? (
          <VideoInfoCaption>
            <StyledIconPlay size={11} />
            {this.props.videoDuration ? <VideoTimeText>{this.props.videoDuration}</VideoTimeText> : null}
          </VideoInfoCaption>
        ) : null}
      </ThumbnailWrap>
    );
  }

  // サイズ指定に応じて適切な画像サイズを返す
  private getImageWidthAndHeight(): [string, string] {
    if (this.props.imageWidth && this.props.imageHeight) {
      // 幅と高さ指定があったらそれを優先
      return [this.props.imageWidth, this.props.imageHeight];
    } else {
      // 基本パターンのサイズ指定がある時
      switch (this.props.imageSize) {
        case ImageSize.SMALL:
          return [size.thumbnail_small_pc, size.thumbnail_small_pc];
        case ImageSize.MIDDLE:
          return [size.thumbnail_middle_pc, size.thumbnail_middle_pc];
        case ImageSize.LARGE:
          return [size.thumbnail_large_pc, size.thumbnail_large_pc];
        case ImageSize.WIDE:
          return [size.thumbnail_wide_width_pc, size.thumbnail_wide_height_pc];
        case ImageSize.WIDE_19X10:
          return [size.thumbnail_wide_19X10_width_pc, size.thumbnail_wide_19X10_height_pc];
        default:
          // エラーハンドリング用に一応nullを返しておく
          return ['', ''];
      }
    }
  }
  // エラー時に掲出する画像のURLを取得
  private getDefaultImageUrl(): string {
    if (this.props.imageWidth && this.props.imageHeight) {
      return ThumbnailHelper.getDefaultImage(this.props.imageWidth, this.props.imageHeight);
    }
    // 基本パターンのサイズ指定がある時
    switch (this.props.imageSize) {
      case ImageSize.WIDE_19X10:
        return DEFAULT_IMAGE_19x10;
      case ImageSize.WIDE:
        return DEFAULT_IMAGE_4x3;
      case ImageSize.SMALL:
      case ImageSize.MIDDLE:
      case ImageSize.LARGE:
      default:
        return DEFAULT_IMAGE_1x1;
    }
  }
}
